import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import TableComponent from "./TableComponent";
import Pagination from "../view/common/pagination/Pagination";
import { updateFilters, resetFilters } from "../redux/slices/filterSlice";
import { RootState } from "../redux/slices/store";
import { getData } from "../api/ApiServices";
import PriceHistoryChangesPopup from "../view/pricehistorypopup";

const PriceHistory: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const filters = useSelector((state: RootState) => state.filters);

  const [priceHistoryData, setPriceHistoryData] = useState<any[]>([]);
  const [originalData, setOriginalData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasNoRecords, setHasNoRecords] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const [sortBy, setSortBy] = useState<string>("updatedAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [isFiltering, setIsFiltering] = useState<boolean>(false);
  const [lastFilterState, setLastFilterState] = useState<any>({});

  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [selectedPartId, setSelectedPartId] = useState<string>("");

  const columns = [
    {
      key: "partDetails",
      label: "Part No",
      nameKey: "internalPartNo",
      render: (value: any) =>
        typeof value === "object" ? value?.internalPartNo : "N/A",
      sortable: true,
    },
    {
      key: "partDetails",
      label: "Part Name",
      nameKey: "partName",
      render: (value: any) =>
        typeof value === "object" ? value?.partName : "N/A",
      sortable: true,
    },
    {
      key: "itemmodel",
      label: "Model",
      nameKey: "name",
      render: (value: any) => (typeof value === "object" ? value?.name : "N/A"),
      sortable: true,
    },
    {
      key: "itemcategory",
      label: "Category",
      nameKey: "name",
      render: (value: any) => (typeof value === "object" ? value?.name : "N/A"),
      sortable: true,
    },
    {
      key: "uoms",
      label: "UOM",
      nameKey: "name",
      render: (value: any) => (typeof value === "object" ? value?.name : "N/A"),
      sortable: true,
    },
    {
      key: "count",
      label: "No of Changes",isRightAlign: true,
      render: (value: number, rowData: any) => (
        <span 
          className="clickable-cell"
          onClick={(e) => {
            e.stopPropagation();
            if (rowData.partDetails && rowData.partDetails._id) {
              handlePriceHistoryChangesClick(rowData.partDetails._id);
            }
          }}
        >
          {value}
        </span>
      ),
      sortable: true,
    },
  ];

  const handlePriceHistoryChangesClick = (partId: string) => {
    setSelectedPartId(partId);
    setIsPopupOpen(true);
  };

  const handleSortChange = (
    sortByField: string,
    sortOrderDirection: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderDirection);
  };

  useEffect(() => {
    fetchPriceHistory();
  }, [currentPage, itemsPerPage, sortBy, sortOrder, filters]);

  const fetchPriceHistory = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response: any = await getData("/pricehistory", {
        page: currentPage,
        limit: itemsPerPage,
        filter: {
          ...filters,
          pricehistorycount: true,
        },
        sortBy: sortBy,
        sortOrder: sortOrder,
      });
      
      console.log("API Response:", response);
      
      if (response?.data?.data) {
        setPriceHistoryData(response.data.data);
        setTotalItems(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.totalPages || 0);
        setHasNoRecords(response.data.data.length === 0);
      } else {
        setPriceHistoryData([]);
        
      }
    } catch (err) {
      console.error("Error fetching price history data:", err);
      setError("Failed to load price history data. Please try again later.");
      setPriceHistoryData([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = useCallback((filters: Record<string, any>) => {
    Object.entries(filters).forEach(([key, value]) => {
      dispatch(updateFilters({ key, value: String(value) }));
    });
  }, [dispatch]);

  return (
    <div className="item-models-container">
      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>Purchase</span>
              <span>Purchase Order</span>
              <span>Price History</span>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="box-container">
        <div className="table-wrapper">
          <TableComponent
            columns={columns}
            data={priceHistoryData}
            setFilter={(filters: Record<string, any>) => {
              Object.entries(filters).forEach(([key, value]) => {
                dispatch(updateFilters({ key, value: String(value) }));
              });
            }}
            setSorting={handleSortChange}
            defaultSortConfig={{ key: sortBy, direction: sortOrder }}
            excludeFields={["updatedBy"]}
            context="priceHistory"
          />
          
          {isLoading && (
            <div className="loading-overlay">
              <div className="spinner"></div>
            </div>
          )}
        </div>
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          totalItems={totalItems}
        />
      </div>

      {/* Price History Changes Popup */}
      <PriceHistoryChangesPopup 
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        partId={selectedPartId}
      />
    </div>
  );
};

export default PriceHistory;